import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BellSimple, CheckCircle, X } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const KIND_TONE = {
  issue_assigned:   { bg: "bg-orange-50",   border: "border-orange-500",   text: "text-orange-800" },
  expense_request:  { bg: "bg-yellow-50",   border: "border-yellow-500",   text: "text-yellow-900" },
  expense_approved: { bg: "bg-green-50",    border: "border-green-500",    text: "text-green-800" },
  expense_rejected: { bg: "bg-red-50",      border: "border-red-500",      text: "text-red-800" },
  info:             { bg: "bg-blue-50",     border: "border-blue-500",     text: "text-blue-800" },
};

export default function NotificationsBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unseen, setUnseen] = useState(0);
  const ref = useRef(null);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/notifications", { params: { limit: 30 } });
      setItems(r.data.items || []);
      setUnseen(r.data.unseen || 0);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        console.debug("Notifications poll failed:", e?.message);
      }
    }
  }, [user]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // click-outside to close
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const markSeen = async (n) => {
    if (n.seen) return;
    setItems(items.map(x => x.id === n.id ? { ...x, seen: true } : x));
    setUnseen(Math.max(0, unseen - 1));
    try { await api.post(`/notifications/${n.id}/seen`); }
    catch (e) { if (process.env.NODE_ENV !== "production") console.debug("mark seen failed:", e?.message); }
  };

  const markAll = async () => {
    setItems(items.map(x => ({ ...x, seen: true })));
    setUnseen(0);
    try { await api.post(`/notifications/mark-all-seen`); }
    catch (e) { if (process.env.NODE_ENV !== "production") console.debug("mark-all-seen failed:", e?.message); }
  };

  const onClickItem = async (n) => {
    await markSeen(n);
    if (n.link) { setOpen(false); nav(n.link); }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notif-bell-btn"
        onClick={() => setOpen(o => !o)}
        title="Notifications"
        className={`relative inline-flex items-center justify-center w-9 h-9 rounded-full transition-colors ${
          unseen > 0
            ? "bg-[color:var(--kr-orange)]/25 text-white hover:bg-[color:var(--kr-orange)]/40"
            : "bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
        }`}
      >
        <BellSimple size={16} weight={unseen > 0 ? "fill" : "bold"} />
        {unseen > 0 && (
          <span
            data-testid="notif-bell-count"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-[color:var(--kr-orange)] text-white text-[10px] font-bold rounded-full font-mono ring-2 ring-[color:var(--kr-nav-bg)]"
          >
            {unseen > 99 ? "99+" : unseen}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 mt-2 w-96 max-h-[70vh] bg-white dark:bg-neutral-900 text-zinc-900 dark:text-white shadow-2xl border border-zinc-200 dark:border-neutral-800 z-50 flex flex-col"
        >
          <div className="px-4 py-3 border-b border-zinc-200 dark:border-neutral-800 flex items-center justify-between bg-zinc-50 dark:bg-neutral-950">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400">Inbox</div>
              <div className="font-bold">Notifications</div>
            </div>
            {unseen > 0 && (
              <button
                onClick={markAll}
                data-testid="notif-mark-all"
                className="text-[10px] font-bold uppercase tracking-widest text-orange-600 hover:text-orange-800"
              >
                Mark all seen
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-zinc-100 dark:divide-neutral-800">
            {items.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-zinc-500">
                <div className="text-3xl mb-2">✓</div>
                You're all caught up.
              </div>
            )}
            {items.map(n => {
              const tone = KIND_TONE[n.kind] || KIND_TONE.info;
              return (
                <button
                  key={n.id}
                  onClick={() => onClickItem(n)}
                  data-testid={`notif-item-${n.id}`}
                  className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-zinc-50 transition-colors ${!n.seen ? tone.bg : "bg-white"}`}
                >
                  <div className={`w-1 rounded-full ${!n.seen ? tone.border.replace("border-", "bg-") : "bg-zinc-200"}`} />
                  <div className="flex-1 min-w-0">
                    <div className={`font-bold text-sm leading-tight ${!n.seen ? tone.text : "text-zinc-500"}`}>
                      {n.title}
                    </div>
                    {n.body && (
                      <div className={`mt-0.5 text-xs ${!n.seen ? "text-zinc-700" : "text-zinc-400"} line-clamp-2`}>
                        {n.body}
                      </div>
                    )}
                    <div className="mt-1 text-[10px] font-mono text-zinc-400">
                      {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                  {!n.seen && (
                    <span
                      title="Mark seen"
                      onClick={(e) => { e.stopPropagation(); markSeen(n); }}
                      className="self-start text-orange-500 hover:text-orange-700"
                    >
                      <CheckCircle size={16} weight="fill" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
